/*! GhostStat — sortable tables.  app.ghoststat.me/js/gs-sort.js
 *
 * Click a column header to sort the rows by it (asc → desc → original). Applies to every table on a
 * plugin page (scoped to <main data-gs-plugin>), so no per-plugin wiring is needed. Opt a table or a
 * column out with data-gs-nosort; the ~handful of plugins that already ship their own sort carry it
 * on the table so this engine defers to them.
 *
 * Design notes:
 *  - Clicks are delegated at document level, so re-rendered tables (tbody replaced on data load)
 *    keep working with no re-binding.
 *  - A guarded MutationObserver re-applies the active sort after an external re-render, so the sort
 *    is "sticky" across date-range / site changes instead of silently snapping back to default.
 *  - Value parsing is type-aware: numbers, %, currency, 1.2k / 3.4M, 250ms / 1.2s, ISO dates. A cell
 *    may override its sort value with data-sort="…". Empty / "—" cells always sort to the bottom.
 * Dependency-free. No-ops when there is no plugin table on the page.
 */
(function (w, d) {
  "use strict";
  if (w.GSSort) return;

  var SCOPE = 'main[data-gs-plugin], .gs-admin';   // plugin pages and Admin (app.ghoststat.me/admin)

  /* Plugins that already ship their own sort (by header click or a sort dropdown). The engine defers
     to them entirely so the two never fight over the same rows. A single table can still opt back in
     with data-gs-sort, or out with data-gs-nosort, regardless of this list. */
  var SKIP = {
    "downloads": 1, "email-analytics": 1, "email-journey": 1, "identity-graph": 1, "outlinks": 1,
    "performanceaudit": 1, "referrers-intelligence": 1, "rollup-reporting": 1, "searches": 1, "seo-visibility": 1
  };

  function isSortableTable(t) {
    if (!t || t.tagName !== "TABLE" || t.hasAttribute("data-gs-nosort") || !t.tHead || !t.tBodies.length) return false;
    var main = t.closest(SCOPE); if (!main) return false;
    if (t.hasAttribute("data-gs-sort")) return true;                    // explicit opt-in wins
    return !SKIP[main.getAttribute("data-gs-plugin")];                  // else honour the skip list
  }
  function headerCells(t) {
    // last header row holds the real column titles (grouped headers put labels on the last row)
    var rows = t.tHead.rows; if (!rows.length) return [];
    return Array.prototype.slice.call(rows[rows.length - 1].cells);
  }
  function colIndexOf(th) {
    // account for colspans in the header row
    var cells = th.parentNode.cells, idx = 0;
    for (var i = 0; i < cells.length; i++) {
      if (cells[i] === th) return idx;
      idx += cells[i].colSpan || 1;
    }
    return -1;
  }
  // a header without a title (an actions or checkbox column) has nothing to sort by
  function thSortable(th) { return th && !th.hasAttribute("data-gs-nosort") && (th.colSpan || 1) === 1 && !/^(|actions?|acties)$/i.test((th.textContent || "").trim()); }

  var DATE_RE = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2})?$|^\d{1,2}\/\d{1,2}\/\d{2,4}$/;

  function rawVal(row, col) {
    var cells = row.cells, seen = 0, i;
    for (i = 0; i < cells.length; i++) {         // map logical column → physical cell (colspans)
      if (seen === col) {
        var td = cells[i];
        if (td.hasAttribute("data-sort")) return td.getAttribute("data-sort");
        // a cell with a field (Admin: free views, on/off switches) sorts by what is filled in or ticked
        var f = td.querySelector("input:not([type=hidden]), select");
        if (f && !(td.textContent || "").trim()) {
          if (f.type === "checkbox" || f.type === "radio") return f.checked ? "1" : "0";
          return f.tagName === "SELECT" ? (f.options[f.selectedIndex] || {}).text || "" : f.value;
        }
        return td.textContent || "";
      }
      seen += cells[i].colSpan || 1;
    }
    return "";
  }
  function isEmpty(s) { s = String(s).trim(); return s === "" || s === "—" || s === "-" || s === "–" || s === "n/a" || s === "N/A"; }

  function toNum(s) {
    s = String(s).trim();
    var neg = /^\(.*\)$/.test(s);                 // (1,234) accounting negative
    var m = s.replace(/[,$€£\s()]/g, "");
    var t = m.match(/^([\d.]+)(ms|s)$/i);         // durations → milliseconds
    if (t) return parseFloat(t[1]) * (t[2].toLowerCase() === "ms" ? 1 : 1000) * (neg ? -1 : 1);
    var mult = 1, suf = m.match(/([kmb])$/i);      // 1.2k / 3.4M / 2B
    if (suf) { mult = { k: 1e3, m: 1e6, b: 1e9 }[suf[1].toLowerCase()]; m = m.slice(0, -1); }
    m = m.replace(/%$/, "");
    var n = parseFloat(m);
    if (isNaN(n)) return NaN;
    n *= mult; if (neg) n = -Math.abs(n);
    return n;
  }

  function colType(rows, col) {
    var num = 0, date = 0, seen = 0;
    for (var i = 0; i < rows.length && seen < 24; i++) {
      var s = String(rawVal(rows[i], col)).trim();
      if (isEmpty(s)) continue;
      seen++;
      if (DATE_RE.test(s)) date++;
      else if (!isNaN(toNum(s))) num++;
    }
    if (!seen) return "text";
    if (date / seen >= 0.6) return "date";
    if (num / seen >= 0.6) return "number";
    return "text";
  }

  function keyOf(row, col, type) {
    var s = String(rawVal(row, col)).trim();
    if (isEmpty(s)) return { empty: true };
    if (type === "date") { var t = Date.parse(s.replace(" ", "T")); return isNaN(t) ? { empty: true } : { v: t }; }
    if (type === "number") { var n = toNum(s); return isNaN(n) ? { empty: true } : { v: n }; }
    return { v: s.toLowerCase() };
  }

  function reindex(t) {
    var b = t.tBodies[0]; if (!b) return;
    Array.prototype.forEach.call(b.rows, function (r, i) { r.__gsi = i; });
  }
  function pinned(r) { return r.hasAttribute("data-gs-keep") || /(^|\s)(total|gs-total)(\s|$)/.test(r.className); }

  function apply(t) {
    var st = t.__gsState; if (!st) return;
    var b = t.tBodies[0]; if (!b) return;
    var rows = Array.prototype.slice.call(b.rows);
    rows.forEach(function (r, i) { if (r.__gsi == null) r.__gsi = i; }); // baseline for any new rows
    var data = [], keep = [];
    rows.forEach(function (r) { (pinned(r) ? keep : data).push(r); });

    if (st.dir === 0) {                            // reset to original render order
      data.sort(function (a, b2) { return (a.__gsi || 0) - (b2.__gsi || 0); });
    } else {
      var type = colType(data, st.col), dir = st.dir;
      data.sort(function (a, b2) {
        var A = keyOf(a, st.col, type), B = keyOf(b2, st.col, type);
        if (A.empty && B.empty) return (a.__gsi || 0) - (b2.__gsi || 0);
        if (A.empty) return 1; if (B.empty) return -1;   // empties always last
        var c = A.v < B.v ? -1 : A.v > B.v ? 1 : 0;
        if (c === 0) return (a.__gsi || 0) - (b2.__gsi || 0); // stable
        return dir * c;
      });
    }
    // Move rows ourselves without waking the sticky observer (which watches for *external* renders).
    if (t.__gsObs) t.__gsObs.disconnect();
    var frag = d.createDocumentFragment();
    data.forEach(function (r) { frag.appendChild(r); });
    keep.forEach(function (r) { frag.appendChild(r); });
    b.appendChild(frag);
    if (t.__gsObs && t.tBodies.length) t.__gsObs.observe(t, { childList: true, subtree: true });
    markAria(t);
  }

  function markAria(t) {
    var st = t.__gsState || { col: -1, dir: 0 };
    headerCells(t).forEach(function (th) {
      var ci = colIndexOf(th);
      if (!thSortable(th)) { th.removeAttribute("aria-sort"); return; }
      th.classList.add("gs-th-sortable");
      if (ci === st.col && st.dir !== 0) th.setAttribute("aria-sort", st.dir === 1 ? "ascending" : "descending");
      else th.removeAttribute("aria-sort");
    });
  }

  function enable(t) {
    if (t.__gsSortOn || !isSortableTable(t)) return;
    t.__gsSortOn = true;
    reindex(t);
    markAria(t);
    // Sticky: re-apply the active sort after an external re-render (data reload). Guarded so our own
    // row moves never re-enter. Observes the whole table so a full innerHTML swap is caught too.
    try {
      var mo = new w.MutationObserver(function () {
        if (t.__gsReflow) return; t.__gsReflow = true;
        (w.requestAnimationFrame || setTimeout)(function () {
          t.__gsReflow = false;
          if (!t.tBodies.length) return;
          reindex(t);                                  // external render → capture new baseline order
          if (t.__gsState && t.__gsState.dir !== 0) apply(t); else markAria(t);
        }, 0);
      });
      mo.observe(t, { childList: true, subtree: true });
      t.__gsObs = mo;
    } catch (e) {}
  }

  function scan() {
    d.querySelectorAll(SCOPE + " table").forEach(enable);
  }

  // one delegated click handler for every current and future sortable header
  d.addEventListener("click", function (ev) {
    var th = ev.target.closest ? ev.target.closest("th") : null;
    if (!th) return;
    var t = th.closest("table");
    if (!t || !isSortableTable(t) || !th.closest("thead") || !thSortable(th)) return;
    if (!t.__gsSortOn) enable(t);
    var col = colIndexOf(th);
    var st = t.__gsState && t.__gsState.col === col
      ? { col: col, dir: t.__gsState.dir === 1 ? -1 : t.__gsState.dir === -1 ? 0 : 1 }
      : { col: col, dir: 1 };
    t.__gsState = st;
    apply(t);
  });

  w.GSSort = { scan: scan, enable: enable };
  if (d.readyState === "loading") d.addEventListener("DOMContentLoaded", scan); else scan();
  d.addEventListener("gs:ready", scan);
  d.addEventListener("gs:site-change", scan);
  // Admin: a section's table is often built when the section opens, so look again shortly after
  d.addEventListener("click", function (ev) {
    if (ev.target.closest && ev.target.closest(".gs-settings-nav a, [data-sec]")) { setTimeout(scan, 400); setTimeout(scan, 1500); }
  });
})(window, document);
